import { useState } from "react";

import { FormControl, TextField, Typography } from "@mui/material";
import { FormikValues } from "formik";

import { useFieldError } from "../forms/missing-fields/hooks/useFieldError";
import { getFieldPath, getFieldValue } from "./FormikField.utils";
import { FormikTextFieldProps } from "./FormikTextField.types";
import { useOptionalLabel } from "./useOptionalLabel";

export function FormikTextField<T extends FormikValues>(props: FormikTextFieldProps<T>) {
  const {
    fieldName,
    path,
    formControlProps,
    formik,
    helperText,
    labelNode,
    labelAbove,
    slotProps,
    required,
    optional,
    label,
    inputRef,
    ...others
  } = props;
  const resolvedLabel = useOptionalLabel(label, optional);
  const [isFocused, setIsFocused] = useState(false);

  const fullPath = getFieldPath(path, fieldName);

  const inputSlotProps = slotProps?.input as { inputProps?: { maxLength?: number } } | undefined;
  const maxLength = inputSlotProps?.inputProps?.maxLength;
  const currentLength = String(getFieldValue(formik, fullPath)).length;

  const error = useFieldError(formik, fullPath, label);
  const characterCountHelper = isFocused && maxLength ? `${currentLength}/${maxLength}` : undefined;
  const resolvedHelperText = error ?? characterCountHelper ?? helperText;

  const inputLabelSlotProps = typeof slotProps?.inputLabel === "object" ? slotProps.inputLabel : {};
  const inputSlot = typeof slotProps?.input === "object" ? slotProps.input : {};
  const formHelperTextSlot = typeof slotProps?.formHelperText === "object" ? slotProps.formHelperText : {};
  const htmlInputSlot = typeof slotProps?.htmlInput === "object" ? slotProps.htmlInput : {};
  const labelId = labelAbove && others.id ? `${others.id}-label` : undefined;

  return (
    <FormControl margin="none" fullWidth {...formControlProps}>
      {labelNode}
      {labelAbove && (
        <Typography id={labelId} variant="caption" sx={{ fontWeight: 500, mb: 0.5 }}>
          {resolvedLabel}
          {required && <span aria-hidden> *</span>}
        </Typography>
      )}
      <TextField
        inputRef={inputRef}
        label={labelAbove ? undefined : resolvedLabel}
        name={fullPath}
        onChange={formik.handleChange}
        onBlur={(e) => {
          setIsFocused(false);
          formik.handleBlur(e);
        }}
        onFocus={() => setIsFocused(true)}
        value={getFieldValue(formik, fullPath)}
        error={!!error}
        helperText={resolvedHelperText ?? " "}
        required={required}
        variant="outlined"
        size="small"
        slotProps={{
          inputLabel: {
            sx: { fontSize: "0.875rem" },
            ...inputLabelSlotProps,
          },
          input: {
            required: false,
            sx: { fontSize: "0.875rem" },
            ...inputSlot,
          },
          formHelperText: formHelperTextSlot,
          htmlInput: { "aria-labelledby": labelId, ...htmlInputSlot },
        }}
        {...others}
      />
    </FormControl>
  );
}
